import { useState } from "react";
import { useLocation } from "react-router-dom";
import ActiveItemDetails from "components/active-item/ActiveItemDetails";
import ActiveItemProperties from "components/active-item/ActiveItemProperties";
import { createAPI } from "services/api/api";
import { useSoundPath } from "hooks/useSoundPath";

const SellPopupConfirm = ({ activeItem, closePopup, nextStep }) => {
  const api = createAPI();
  const [sellAmount, setSellAmount] = useState("");
  const { pathname } = useLocation();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const playSound = useSoundPath();

  const getNumericValue = (str) => {
    if (!str || typeof str !== "string") return NaN;
    const normalized = str.trim().replace(/,/g, ".");
    return parseFloat(normalized);
  };

  const setAmountHandler = (e) => {
    const { value } = e.target;

    const cleanedValue = value.replace(/[^0-9.,]/g, "");
    const parts = cleanedValue.split(/[,\.]/);

    if (parts.length > 2 || (parts.length === 2 && parts[1].length > 2)) {
      return;
    }

    setSellAmount(cleanedValue);
  };

  const handleBlur = () => {
    const current = sellAmount.trim();
    if (current === "") {
      setSellAmount("");
      return;
    }

    const num = getNumericValue(current);
    if (isNaN(num)) {
      setSellAmount("");
      return;
    }

    setSellAmount(num.toFixed(2));
  };

  const isSubmitDisabled = () => {
    const num = getNumericValue(sellAmount);
    return isNaN(num) || num < 1 || isSubmitLoading;
  };

  const getTypeOfItem = () => {
    const properties = activeItem.properties;
    const isPackType = properties.find((prop) => prop.title === "PackType");
    const isType = properties.find((prop) => prop.title === "Type");
    if (isPackType) return isPackType.text + " Pack";
    if (isType) return isType.text + " Card";
  };

  const submitHandle = () => {
    const num = getNumericValue(sellAmount);
    if (isNaN(num)) return;

    setIsSubmitLoading(true);
    playSound("main");
    const payload = {
      marketplace_sell: [{listing_id: activeItem.marketplace_id, price: num}]
    };
    api
      .post(`/marketplace_listing/sell`, payload)
      .then(() => nextStep())
      .catch((error) => {
        console.error(error);
        nextStep("error");
      })
      .finally(() => setIsSubmitLoading(false));
  };

  const numericAmount = getNumericValue(sellAmount);
  const fee = isNaN(numericAmount) ? 0 : numericAmount * 0.05;
  const net = isNaN(numericAmount) ? 0 : numericAmount * 0.95;

  return (
    <section className="sell-card" onClick={(e) => e.stopPropagation()}>
      <section className="sell-card__details">
        <ActiveItemDetails
          pathname={pathname}
          name={activeItem.name}
          img={activeItem.image}
          packType={activeItem.pack_type}
          tag={activeItem.tag}
          type={activeItem.type}
          properties={activeItem.properties}
        />
        <ActiveItemProperties properties={activeItem.properties} />
      </section>
      <section className="sell-card__info">
        <header className="sell-card__header">
          <h2 className="sell-card__title">Sell {getTypeOfItem()} </h2>
          <button className="sell-card__close" onClick={closePopup}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </button>
        </header>
        <form className="sell-card__info-form">
          <p className="sell-card__info-row">
            <label className="sell-card__info-label">
              Enter a listing price.
            </label>
            <input
              type="text"
              className="sell-card__info-input"
              placeholder="$0.00"
              value={sellAmount}
              onInput={setAmountHandler}
              onBlur={handleBlur}
            />
            <span className="sell-card__info-tip">USD</span>
          </p>
          <ul className="sell-card__info-price">
            <li className="sell-card__info-price-title">Price summary</li>
            <li className="sell-card__info-price-item">
              <span>Listing price</span>
              <span>
                ${isNaN(numericAmount) ? "0.00" : numericAmount.toFixed(2)}
              </span>
            </li>
            <li className="sell-card__info-price-item">
              <span>Marketplace Fee (5%)</span>
              <span>${fee.toFixed(2)}</span>
            </li>
            <li className="sell-card__info-price-item">
              <span>Net amount to seller</span>
              <span>${net.toFixed(2)}</span>
            </li>
          </ul>
          <button
            className={`sell-card__info-form-submit ${
              isSubmitLoading ? "loading" : ""
            }`}
            onClick={submitHandle}
            disabled={isSubmitDisabled()}
            type="button"
          >
            List for sale
          </button>
        </form>
      </section>
    </section>
  );
};

export default SellPopupConfirm;
